/**
 * RunRibbon — a finished drafting batch in the Chat tab. When a batch ends,
 * its mini spine (RunCapsule) collapses into this one line at the same point
 * in the chat: what was drafted, what needs review, how long it took. Every
 * batch leaves its own ribbon, so a chat with three "Draft more sections"
 * passes shows three ribbons, each where its batch ran. Tapping one opens
 * the Spine.
 *
 * The batch's own chat rows (activation / trial-context notes, its web
 * searches) are folded into the ribbon: when it has any, tapping the ribbon
 * expands it to list them in order, each with its time, and the Spine link
 * moves into the expanded view.
 *
 * SRP: presentation only. The text comes from ``ribbonText``.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { ChevronRight12Regular } from "@fluentui/react-icons";
import type React from "react";
import { useState } from "react";
import type { RunCompleteSummary } from "../messageProjection";
import { type RunStep, ribbonText } from "../runCapsuleModel";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";

export interface RunRibbonProps {
  summary: RunCompleteSummary | null;
  /** The batch ended without finishing (failed, or stopped). */
  stopped: boolean;
  onOpenSpine?: () => void;
  /** The batch's folded chat rows, oldest first. None → a plain link. */
  steps?: readonly RunStep[];
  /** Start expanded (previews / tests). */
  defaultExpanded?: boolean;
}

const useStyles = makeStyles({
  ribbon: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "7px 10px",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    background: "#fff",
    fontFamily: fonts.body,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { border: `1px solid ${surfaceColors.lineStrong}` },
    ":disabled": { cursor: "default" },
  },
  mark: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    background: statusColors.cool,
  },
  markStopped: {
    width: "14px",
    height: "14px",
    boxSizing: "border-box",
    background: "#fff",
    border: `1.5px solid ${statusColors.danger}`,
    color: statusColors.danger,
    fontSize: "8px",
    fontWeight: 800,
    lineHeight: 1,
  },
  title: {
    flexShrink: 0,
    fontSize: "12.5px",
    fontWeight: 600,
    color: inkColors.ink2,
    whiteSpace: "nowrap",
  },
  meta: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.04em",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  spacer: { flex: 1 },
  open: {
    display: "flex",
    flexShrink: 0,
    color: inkColors.ink4,
    transitionProperty: "transform",
    transitionDuration: "120ms",
  },
  openExpanded: { transform: "rotate(90deg)" },
  wrap: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    background: "#fff",
    overflow: "hidden",
  },
  ribbonInWrap: {
    border: "none",
    borderRadius: 0,
    ":hover": { border: "none", background: surfaceColors.paperDim },
  },
  steps: {
    margin: 0,
    padding: "4px 10px 6px 34px",
    listStyle: "none",
    borderTop: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
  },
  step: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    padding: "3px 0",
    fontSize: "11.5px",
    lineHeight: 1.35,
    color: inkColors.ink3,
    minWidth: 0,
  },
  stepKind: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: inkColors.ink4,
  },
  stepText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stepTime: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 10px 7px",
  },
  openSpine: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    padding: 0,
    border: "none",
    background: "none",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink3,
    cursor: "pointer",
    ":hover": { color: inkColors.ink },
  },
});

/** Local "HH:MM" (the chat's message-time format). */
function clockTime(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function stepKindLabel(step: RunStep): string {
  if (step.kind === "search") return "Web";
  if (step.kind === "tool") return step.label ?? "Tool";
  return "Note";
}

/** White check inside the green disc (the Spine's done glyph). */
const CHECK_GLYPH = (
  <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      d="M2.5 6.3 5 8.7 9.5 3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RunRibbon: React.FC<RunRibbonProps> = ({
  summary,
  stopped,
  onOpenSpine,
  steps = [],
  defaultExpanded = false,
}) => {
  const styles = useStyles();
  const { title, meta } = ribbonText(summary, stopped);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const mark = (
    <span className={mergeClasses(styles.mark, stopped && styles.markStopped)} aria-hidden="true">
      {stopped ? "✕" : CHECK_GLYPH}
    </span>
  );
  const label = meta ? `${title}, ${meta}` : title;

  if (steps.length === 0) {
    return (
      <button
        type="button"
        className={styles.ribbon}
        onClick={onOpenSpine}
        disabled={!onOpenSpine}
        data-testid="run-ribbon"
        data-state={stopped ? "stopped" : "done"}
        title="Open Spine"
        aria-label={`${label}. Open Spine`}
      >
        {mark}
        <span className={styles.title}>{title}</span>
        {meta && <span className={styles.meta}>{meta}</span>}
        <span className={styles.spacer} />
        {onOpenSpine && <ChevronRight12Regular className={styles.open} aria-hidden="true" />}
      </button>
    );
  }

  const stepCount = `${steps.length} ${steps.length === 1 ? "step" : "steps"}`;
  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={mergeClasses(styles.ribbon, styles.ribbonInWrap)}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="run-ribbon"
        data-state={stopped ? "stopped" : "done"}
        title={expanded ? "Hide steps" : "Show steps"}
        aria-label={`${label}. ${stepCount}`}
      >
        {mark}
        <span className={styles.title}>{title}</span>
        {meta && <span className={styles.meta}>{meta}</span>}
        <span className={styles.spacer} />
        <ChevronRight12Regular
          className={mergeClasses(styles.open, expanded && styles.openExpanded)}
          data-testid="run-ribbon-chevron"
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <>
          <ul className={styles.steps} data-testid="run-ribbon-steps" aria-label="Run steps">
            {steps.map((step) => (
              <li key={step.key} className={styles.step} data-testid="run-ribbon-step" data-kind={step.kind}>
                <span className={styles.stepKind}>{stepKindLabel(step)}</span>
                <span className={styles.stepText} title={step.text}>
                  {step.text}
                </span>
                <span className={styles.stepTime}>{clockTime(step.at)}</span>
              </li>
            ))}
          </ul>
          {onOpenSpine && (
            <div className={styles.footer}>
              <button type="button" className={styles.openSpine} onClick={onOpenSpine}>
                Open Spine
                <ChevronRight12Regular aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RunRibbon;
